import { FC, useMemo, useState } from 'react';

import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftSupplySettlement } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';

import { formatDate } from '../../giftSeller/utils/delivery.utils';
import { formatWon, roundUpTo10 } from '../../giftSeller/utils/settlement.utils';
import { getKstDateParts, MONTHS, statusColor, statusLabel } from '../utils/settlement.utils';
import { SupplySettlementDetailModal } from './SupplySettlementDetailModal';

// ─────────────────────────────────────────────
// HistoryYearCalendar
// ─────────────────────────────────────────────

/** `HistoryYearCalendar` 컴포넌트의 props */
export interface HistoryYearCalendarProps {
  /** 정산 완료 내역 목록 (undefined이면 로딩 전 상태) */
  historyItems: GiftSupplySettlement[] | undefined;
  /** 데이터 로딩 중 여부 */
  loading: boolean;
  /**
   * 공급사 본인의 정산정보.
   * `splitLineSurcharge`에서 소량 추가단가/택배비 분해 시 사용한다.
   * 관리자 뷰에서는 `undefined`를 전달한다.
   */
  mySettlementInfo?: SettlementInfo;
}

/**
 * 정산 완료 내역을 연도별 12개월 캘린더 형태로 렌더링하는 컴포넌트.
 *
 * - 연도 선택기(select)로 조회 연도를 전환할 수 있다.
 * - 정산 데이터가 있는 월은 클릭 시 라인별 상세(상품/단가/수량/택배비/정산금액)를 펼쳐 보여준다.
 * - 정산 데이터가 없는 월은 '미정' 상태로 빈 행을 표시한다.
 * - 하단 합계 행에 선택 연도의 총 건수와 총 정산금액을 표시한다.
 */
export const HistoryYearCalendar: FC<HistoryYearCalendarProps> = ({
  historyItems,
  mySettlementInfo,
  loading,
}) => {
  const currentYear = useMemo(() => getKstDateParts().year, []);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  /** 상세 모달에 표시할 정산 문서 (null이면 닫힘) */
  const [detailItem, setDetailItem] = useState<GiftSupplySettlement | null>(null);

  const yearOptions = useMemo(() => {
    const years = new Set<number>([currentYear]);
    (historyItems ?? []).forEach((s) => years.add(s.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [historyItems, currentYear]);

  /** 선택 연도의 month → GiftSupplySettlement 맵 */
  const monthMap = useMemo(() => {
    const map = new Map<number, GiftSupplySettlement>();
    (historyItems ?? []).forEach((s) => {
      if (s.year === selectedYear) map.set(s.month, s);
    });
    return map;
  }, [historyItems, selectedYear]);

  const yearTotalCount = useMemo(
    () => Array.from(monthMap.values()).reduce((sum, s) => sum + s.lines.length, 0),
    [monthMap],
  );
  const yearTotalAmount = useMemo(
    () => Array.from(monthMap.values()).reduce((sum, s) => sum + s.totalAmount, 0),
    [monthMap],
  );

  return (
    <div>
      {/* 연도 선택 */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">연도</span>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="rounded border border-gray-300 px-2 py-1"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}년
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="center-box py-8">
          <Loading />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">정산월</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">지급일</th>
                <th className="px-3 py-2 text-right">건수</th>
                <th className="px-3 py-2 text-right">정산금액(VAT 포함)</th>
                <th className="px-3 py-2 text-center">상세내역</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {MONTHS.map((m) => {
                const s = monthMap.get(m);

                if (!s) {
                  return (
                    <tr key={m} className="text-gray-400">
                      <td className="whitespace-nowrap px-3 py-3 font-medium">
                        {selectedYear}년 {m}월
                      </td>
                      <td className="px-3 py-3">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          미정
                        </span>
                      </td>
                      <td className="px-3 py-3">-</td>
                      <td className="px-3 py-3 text-right">-</td>
                      <td className="px-3 py-3 text-right">-</td>
                      <td className="px-3 py-3 text-center">-</td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={s._id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setDetailItem(s)}
                  >
                    <td className="whitespace-nowrap px-3 py-3 font-medium">
                      {s.year}년 {s.month}월
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(s.status)}`}
                      >
                        {statusLabel(s.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                      {s.executedAt ? (
                        <div className="flex items-center gap-1.5">
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                            완료
                          </span>
                          {formatDate(s.executedAt)}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-600">
                            예정
                          </span>
                          {s.month === 12 ? s.year + 1 : s.year}년{' '}
                          {s.month === 12 ? 1 : s.month + 1}월 15일
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                      {s.lines.length}건
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                      {formatWon(s.totalAmount)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100">
                        상세보기
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-sm font-semibold">
              <tr>
                <td className="px-3 py-3 text-gray-500">{selectedYear}년 합계</td>
                <td className="px-3 py-3" />
                <td />
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {yearTotalCount}건
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
                  {formatWon(roundUpTo10(yearTotalAmount))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* 정산 상세 모달 */}
      <SupplySettlementDetailModal
        settlement={detailItem}
        mySettlementInfo={mySettlementInfo}
        onClose={() => setDetailItem(null)}
      />
    </div>
  );
};
